import { all, put, select, takeEvery } from "redux-saga/effects";
import { findKey, get, last, flatten, uniqBy, head, filter } from 'lodash';

import {
  fetchSubRegions,
  geoFencingSearchLocationApi,
  geoFencingSavedLocationApi
} from '../../../../helpers/geoFencingApi';
import * as Actions from './actions';
import constants from '../../../../helpers/constant';
import { SELECTION_LOADINGSTATUS, TYPE_STANDARD } from '../../../../components/Geofence/constant';
import { showToast } from '../notification';

export const getLastIdPart = (id) => {
  return id.match(/^([a-zA-Z\d]+-)*([a-zA-Z\d]+)$/)[2];
}

function* setSelection(action) {
  if(action.payload.hasNewRegion){
    const state = yield select( state =>  state[Actions.namespace]);
    const selectedIds = action.payload.selectedIds;
    const { loadedRegions, selectionType } = state;
    yield* selectedIds.map( function* (id){
      const regionId = getLastIdPart(id);
      const regionLevel = findKey(loadedRegions, item => item[regionId]);
      const loadingStatus = get(loadedRegions, [regionLevel, regionId, SELECTION_LOADINGSTATUS[selectionType]]);
      if( loadingStatus === Actions.Status.STATUS_INITIAL || loadingStatus === Actions.Status.STATUS_ERROR){
        yield put(Actions.loadSubRegion({
          id: regionId,
          level: parseInt(regionLevel.match(/^(.*)(\d)$/)[2]),
          type: selectionType
        }));
      }
    });
  }
};

function* loadSubRegion(action) {
  const geoFenceState = yield select( state =>  state[Actions.namespace]);
  const selectionTypeState = get(geoFenceState, 'selectionType');
  const config = yield select( state =>  state.config);
  const { id, level, type } = action.payload;
  const queryId = id === Actions.REGION_WORLD? id : getLastIdPart(id);
  const selectionType = type || selectionTypeState;
  const fetchResults = yield fetchSubRegions(config.urlApi, {parentId:queryId, selectionType});
  if (fetchResults && fetchResults.code === Actions.FetchResult.FETCH_SUCCESS) {
    yield put(Actions.setRegionData({
      data: fetchResults.data,
      type: selectionType,
      parentId: id,
      parentLevel: level,
      lastParentId: queryId
    }));
  } else {
    const toast = {
      message: "Something went wrong",
      action: "Dismiss",
      type: constants.FAILED
    };
    yield put(showToast(toast));
  }
};

function* getSearchData(action) {
  const { searchValue } = action.payload;
  const config = yield select( state =>  state.config);
  const currentSearch = {
    query: searchValue,
    status: Actions.Status.STATUS_LOADING,
  }
  yield put(Actions.setSearchResult(currentSearch));
  //TODO: save latest search -> const catchData = {};
  const state = yield select( state => state[Actions.namespace]);
  const { loadedRegions, selectionType, allUnratedIds } = state;
  const unratedLocationIds = allUnratedIds.map(id => Number(getLastIdPart(id)));
  const fetchResults = yield geoFencingSearchLocationApi(config.urlApi, { searchValue, selectionType, unratedLocationIds });       
  if(fetchResults && fetchResults.code === Actions.FetchResult.FETCH_SUCCESS){
    currentSearch.data = fetchResults.data;
    //the result might be array of arrarys if there are more than one country matched serach query
    //TODO: optimize this
    const fetchData = uniqBy(flatten(fetchResults.data), 'criterial_id');
    yield* fetchData.map( function* (region) {
      const { parentIdTree, criterial_id, type } = region;
      const regionLevel = findKey(loadedRegions, item => item[criterial_id]);
      const regionData = get(loadedRegions, [regionLevel, criterial_id]);
      const loaded = !!regionData && regionData.fetchType === selectionType;

      if (!loaded && type !== 'country') {
        const lastParentId = last(parentIdTree);
        yield put(Actions.setRegionData({
          data: {[lastParentId]:[region]},
          parentId: parentIdTree.join('-'),
          parentLevel: parentIdTree.length,
          lastParentId,
          type: selectionType,
          keepLoadingInitial: true  //if data came from search, keep loadingStatus initial
        }));
      }
    })
    currentSearch.status = Actions.Status.STATUS_LOADED
  } else {
    currentSearch.status = Actions.Status.STATUS_ERROR;
    const toast = {
      message: "Something went wrong",
      action: "Dismiss",
      type: constants.FAILED
    };
    yield put(showToast(toast));
  }
  yield put(Actions.setSearchResult(currentSearch));
};

function* loadSavedLocations(action) {
  const { selectedIds, selectionType } = action.payload;
  const selectedIdData = selectedIds.map(ids => getLastIdPart(ids));
  const config = yield select( state =>  state.config);
  const fetchResults = yield geoFencingSavedLocationApi(config.urlApi, { selectedIdData, selectionType });
  if (fetchResults.code === Actions.FetchResult.FETCH_SUCCESS) {
    const allLocations = uniqBy(flatten(fetchResults.data), 'criterial_id')
    for (let i = 0; i < allLocations.length; i++) {
      const location = allLocations[i];
      if (location.parentIdTree) { //not set level 1
        const { parentIdTree } = location;
        const lastParentId = last(parentIdTree);
        yield put(Actions.setRegionData({
          data: {[lastParentId]:[location]},
          parentId: parentIdTree.join('-'),
          parentLevel: parentIdTree.length,
          lastParentId,
          keepLoadingInitial: true //if data came from editing, keep loadingStatus initial
        }));
      }
    }
  } else {
    const toast = {
      message: "Something went wrong",
      action: "Dismiss",
      type: constants.FAILED
    };
    yield put(showToast(toast));
  }
};

function* preloadUnratedMarket() {
  const geoState = yield select(state => state[Actions.namespace]);
  const selectionType = get(geoState, 'selectionType');
  const unratedIds = get(geoState, 'allUnratedIds', []);
  const unratedIdsHashMap = unratedIds.reduce((accum, ids) => {
    const idPart = getLastIdPart(ids);
    accum[idPart] = true;
    return accum;
  }, {});
  const config = yield select( state =>  state.config);
  const fetchResults = yield geoFencingSavedLocationApi(
    config.urlApi, 
    { 
      selectedIdData: Object.keys(unratedIdsHashMap), 
      selectionType: TYPE_STANDARD 
    }
  );
  if (fetchResults.code === Actions.FetchResult.FETCH_SUCCESS) {
    const unratedLocation = filter(
      flatten(fetchResults.data), 
      location => unratedIdsHashMap[location.criterial_id]
    );

    for (let i = 0, len = unratedLocation.length; i < len; i++) {
      const location = unratedLocation[i];
      const { parentIdTree } = location;
      const parentId = head(parentIdTree); // only get country as parent
      if (parentId) {
        const newParentIdTree = [parentId];
        location.parentIdTree = newParentIdTree;
        yield put(Actions.setRegionData({
          data: {[parentId]:[location]},
          type: selectionType,
          parentId,
          parentLevel: newParentIdTree.length,
          lastParentId: parentId,
          isUnrated: true,
          keepLoadingInitial: true
        }));
      }
    }

    // preload unrated children
    yield* fetchResults.data && fetchResults.data.map( function* (regionGroup) { 
      const unratedRegion = last(regionGroup);
      const { criterial_id } = unratedRegion;
      const parentId = criterial_id.toString();
      const subRegions = yield fetchSubRegions(config.urlApi, {parentId, selectionType: TYPE_STANDARD});
      if (subRegions && subRegions.code === Actions.FetchResult.FETCH_SUCCESS) {
        const subRegionData = get(subRegions, ['data', parentId], [])
        for (let i = 0, len = subRegionData.length; i < len; i++) {
          const currentRegion = subRegionData[i];
          const { parentIdTree } = currentRegion;
          const headParentId = head(parentIdTree);
          const newRegionParentTree = [headParentId, criterial_id];
          currentRegion.parentIdTree = newRegionParentTree;
          currentRegion[selectionType] = true;
          yield put(Actions.setRegionData({
            data: {[parentId]: [currentRegion]},
            type: selectionType,
            parentId: parentId,
            parentLevel: 2,
            lastParentId: parentId,
            isUnrated: true,
            keepLoadingInitial: true
          }));
        };
      }
    });
  } else {
    const toast = {
      message: 'Load unrated market failed',
      action: 'Dismiss',
      type: constants.FAILED
    };
    yield put(showToast(toast));
  }
}

export default function* geoSelector() {
  const ActionType = Actions.ActionType;

  yield all([
    takeEvery(ActionType.SET_SELECTION, setSelection),
    takeEvery(ActionType.LOAD_SUB_REGION, loadSubRegion),
    takeEvery(ActionType.GET_SEARCH_DATA, getSearchData),
    takeEvery(ActionType.LOAD_SAVED_LOCATIONS, loadSavedLocations),
    takeEvery(ActionType.LOAD_UNRATED_MARKET, preloadUnratedMarket)
  ]);
};
